import React, { useState, useEffect } from 'react';
import manSettings from '../data/man_userSettings.json';
import womanSettings from '../data/woman_userSettings.json';
import filtersData from '../data/filters.json';
import './SettingsPage.css';
import { usePov } from '../context/PovContext';

interface UserSettings {
  profile: {
    catchPhrase: string;
    opener?: string;
  };
  personalAttributes: {
    age: number;
    height: number;
    education: string;
    occupation: string;
    interests: string[];
    lifestyle: string[];
    lookingFor: string[];
  };
}

interface FilterData {
  id: string;
  type: 'range' | 'multiselect';
  label: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  options?: string[];
  defaultValue: number[] | string[];
}

const SettingsPage: React.FC = () => {
  const { pov } = usePov();
  const initial = pov === 'woman' ? womanSettings : manSettings;
  const [settings, setSettings] = useState<UserSettings>(initial as UserSettings);

  const handleProfileChange = (field: keyof UserSettings['profile'], value: string) => {
    setSettings(prev => ({
      ...prev,
      profile: {
        ...prev.profile,
        [field]: value
      }
    }));
  };

  const handleAttributeChange = (field: string, value: number | string | string[]) => {
    setSettings(prev => ({
      ...prev,
      personalAttributes: {
        ...prev.personalAttributes,
        [field]: value
      }
    }));
  };

  const handleSingleSelectChange = (field: string, option: string) => {
    setSettings(prev => ({
      ...prev,
      personalAttributes: {
        ...prev.personalAttributes,
        [field]: option
      }
    }));
  };

  const handleMultiSelectChange = (field: string, option: string) => {
    setSettings(prev => {
      const currentSelection = prev.personalAttributes[field as keyof typeof prev.personalAttributes] as string[];
      const newSelection = currentSelection.includes(option)
        ? currentSelection.filter(item => item !== option)
        : [...currentSelection, option];
      
      return {
        ...prev,
        personalAttributes: {
          ...prev.personalAttributes,
          [field]: newSelection
        }
      };
    });
  };

  const renderSingleRangeSlider = (field: string, label: string, min: number, max: number, unit: string) => {
    const value = settings.personalAttributes[field as keyof typeof settings.personalAttributes] as number;
    const percent = ((value - min) / (max - min)) * 100;

    return (
      <div className="setting-item">
        <div className="setting-header">
          <h3>{label}</h3>
          <span className="setting-value">{value} {unit}</span>
        </div>
        <div className="single-range-container">
          <div className="single-range-wrapper">
            <div className="single-range-track">
              <div 
                className="single-range-fill"
                style={{ width: `${percent}%` }}
              />
            </div>
            <input
              type="range"
              min={min}
              max={max}
              step={1}
              value={value}
              onChange={(e) => handleAttributeChange(field, parseInt(e.target.value))}
              className="single-range-input"
            />
          </div>
          <div className="range-bounds">
            <span>{min} {unit}</span>
            <span>{max} {unit}</span>
          </div>
        </div>
      </div>
    );
  };

  const renderSingleSelect = (field: string, label: string, options: string[]) => {
    const selectedValue = settings.personalAttributes[field as keyof typeof settings.personalAttributes] as string;

    return (
      <div className="setting-item">
        <div className="setting-header">
          <h3>{label}</h3>
          <span className="setting-count">1 selected</span>
        </div>
        <div className="single-select-container">
          {options.map((option) => (
            <button
              key={option}
              className={`option-chip ${selectedValue === option ? 'selected' : ''}`}
              onClick={() => handleSingleSelectChange(field, option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    );
  };

  const renderMultiSelect = (field: string, label: string, options: string[]) => {
    const selectedValues = settings.personalAttributes[field as keyof typeof settings.personalAttributes] as string[];

    return (
      <div className="setting-item">
        <div className="setting-header">
          <h3>{label}</h3>
          <span className="setting-count">{selectedValues.length} selected</span>
        </div>
        <div className="multi-select-container">
          {options.map((option) => (
            <button
              key={option}
              className={`option-chip ${selectedValues.includes(option) ? 'selected' : ''}`}
              onClick={() => handleMultiSelectChange(field, option)}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="settings-page">
      <div className="settings-header">
        <h1>Profile</h1>
      </div>
      
      <div className="settings-container">
        <div className="setting-item">
          <div className="setting-header">
            <h3>Catch Phrase</h3>
          </div>
          <textarea
            className="profile-textarea"
            value={settings.profile.catchPhrase}
            onChange={(e) => handleProfileChange('catchPhrase', e.target.value)}
            placeholder="Your catchy profile tagline..."
            rows={2}
          />
        </div>

        {pov === 'man' && (
          <div className="setting-item">
            <div className="setting-header">
              <h3>Opener</h3>
            </div>
            <textarea
              className="profile-textarea"
              value={settings.profile.opener || ''}
              onChange={(e) => handleProfileChange('opener', e.target.value)}
              placeholder="Your opening message for blind dates..."
              rows={3}
            />
          </div>
        )}
        
        {renderSingleRangeSlider('age', 'Age', 18, 65, 'years')}
        {renderSingleRangeSlider('height', 'Height', 150, 200, 'cm')}
        
        {/* Single select attributes */}
        {renderSingleSelect('education', 'Education', 
          filtersData.find(f => f.id === 'education')?.options || [])}
        {renderSingleSelect('occupation', 'Occupation', 
          filtersData.find(f => f.id === 'occupation')?.options || [])}
        
        {/* Multi select attributes */}
        {renderMultiSelect('interests', 'Interests', 
          filtersData.find(f => f.id === 'interests')?.options || [])}
        {renderMultiSelect('lifestyle', 'Lifestyle', 
          filtersData.find(f => f.id === 'lifestyle')?.options || [])}
        {renderMultiSelect('lookingFor', 'Looking For', 
          filtersData.find(f => f.id === 'relationship_type')?.options || [])}
      </div>
    </div>
  );
};

export default SettingsPage;
